import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { shouldShowMoreShow, ShowMore } from 'app/Components/common/ShowMore';
import {
  DrawerCloseIcon,
  DrawerHeaderRowTitle
} from 'app/Components/common/Drawer/styles';
import { TError, TFetchStatus } from 'models';
import { renderErrors } from 'app/Components/common/Error';
import { Loader } from 'styles/common';
import {
  DrawerBodyAdmin,
  GroupStyled,
  TeamsListStyled,
  GroupName,
  GroupControls,
  GroupStyledInner,
  GroupStyledInnerMain,
  InputName
} from '../styles';
import {
  processAPIError,
  makeRequestQuery,
  preflightAPICall,
  translate
} from 'utils';
import LoadingOverlay from 'helpers/LoadingOverlay';
import { scrollElementToBottom, scrollElementToTop } from 'app/Components/common/Drawer';
import { handleAddGroup, handlePatchGroup, TBrokerGroup, TGroupListData } from '../../index';
import { Button, Input } from '@insly/qmt-reactjs-ui-lib';
import { API_GROUP_LIST_URL } from 'config/api';

type TGroup = {
  domainTag: string,
  groupTitle: string,
  subGroup?: TGroup,
};

const DOMAIN_TAG_TEAM = 'team';
const GROUP_TITLE_TEAM = 'Teams';

const DOMAIN_TAG_DEPARTMENT = 'department';

interface IProps {
  selectedBrokerGroup: TBrokerGroup | null,
  handleDrawerClose: () => void,
  dataStatus: TFetchStatus,
}

//@TODO: Update Drawer Lib component and make children not required and remove unnecessary <div />
const DrawerProfile = ({
  selectedBrokerGroup,
  handleDrawerClose,
  dataStatus,
}: IProps) => {

  switch (dataStatus) {
    case 'failed':
      return (
        <>
          {renderErrors([])}
        </>
      );
    case 'success':
      return (selectedBrokerGroup ? renderDrawerInner(selectedBrokerGroup, handleDrawerClose) : <div />);
    case 'loading':
    default:
      return <Loader />;
  }
};

const renderDrawerInner = (
  selectedBrokerGroup: TBrokerGroup,
  handleDrawerClose: () => void,
) => (
  <>
    <DrawerBody brokerGroupData={selectedBrokerGroup} handleDrawerClose={handleDrawerClose} />
  </>
);

const DrawerBody = ({
  brokerGroupData,
  handleDrawerClose
}: {
  brokerGroupData: TBrokerGroup,
  handleDrawerClose: () => void,
}) => {
  const bodyEl = useRef<HTMLDivElement>(null);
  const [showMore, updateShowMore] = useState(false);

  useEffect(() => {
    if (bodyEl.current !== null) {
      scrollElementToTop(bodyEl.current);
      shouldShowMoreShow(bodyEl.current, showMore, updateShowMore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerGroupData.id]);

  const onBodyScroll = () => {
    if (bodyEl.current !== null) {
      shouldShowMoreShow(bodyEl.current, showMore, updateShowMore);
    }
  };

  return (
    <DrawerBodyAdmin ref={bodyEl} onScroll={onBodyScroll} showMore={true}>
      <DrawerHeaderRowTitle>
        <div>
          <div>{translate({ key: 'master.brokers.structure.branch.title' })}: {brokerGroupData.title}</div>
          <div>{translate({ key: 'master.common.title' })}: {brokerGroupData.name}</div>
        </div>
        <DrawerCloseIcon icon="close" onClick={handleDrawerClose} />
      </DrawerHeaderRowTitle>
      <GroupList
        parentId={brokerGroupData?.id as string}
        domainTag={DOMAIN_TAG_DEPARTMENT}
        subGroup={{
          domainTag: DOMAIN_TAG_TEAM,
          groupTitle: GROUP_TITLE_TEAM,
        }}
      />
      <ShowMore show={showMore} onClick={() => scrollElementToBottom(bodyEl.current)} />
    </DrawerBodyAdmin>
  );
};

const GroupList = ({
  parentId,
  domainTag,
  subGroup,
} : {
  parentId: string,
  domainTag: string,
  subGroup?: TGroup,
}) => {
  const [fetchStatus, updateFetchStatus] = useState<TFetchStatus>('loading');
  const [itemList, updateItemList] = useState<TGroupListData>({});
  const [errors, updateErrors] = useState<TError[]>([]);
  const [isSubmitting, updateIsSubmitting] = useState(false);

  useEffect(() => {
    getList({});
    // eslint-disable-next-line
  }, [parentId]);

  const getList = ({ page }: { page?: number }) => {
    updateFetchStatus('loading');
    let query: Record<string, string | number> = {};

    query.page_size = 50;

    if (page) {
      query.page = page;
    }

    if (parentId) {
      query.parent_id = parentId;
    }

    query.domain_tag = domainTag;

    const queryString = makeRequestQuery(query);

    preflightAPICall(() => {
      axios.get(`${API_GROUP_LIST_URL}${queryString}`).then(response => {
        const data = response.data;
        updateItemList(data);
        updateFetchStatus('success');

      }).catch(error => {
        updateFetchStatus('failed');
        updateItemList({
          results: [],
        });

        updateErrors(processAPIError(error, true, 'masterService') as TError[]);

      });
    });
  };

  const onGroupAdded = (brokerGroup: TBrokerGroup) => {
    let list = itemList.results ? [brokerGroup, ...itemList.results] : [brokerGroup];

    updateItemList({
      ...itemList,
      results: list
    });
    updateIsSubmitting(false);
  };

  const onGroupUpdated = (brokerGroup: TBrokerGroup) => {
    const list = itemList.results ? [...itemList.results] : [brokerGroup];
    const brokerIndex = list.findIndex(i => i.id === brokerGroup.id);

    if (brokerIndex !== -1) {
      list[brokerIndex] = brokerGroup;
    }

    updateItemList({
      ...itemList,
      results: list
    });
    updateIsSubmitting(false);
  };

  switch (fetchStatus) {
    case 'failed':
      return (
        <>
          {renderErrors(errors)}
        </>
      );
    case 'success':
      return (
        <>
          {isSubmitting ? <LoadingOverlay /> : null}
          <GroupItems
            parentId={parentId}
            domainTag={domainTag}
            subGroup={subGroup}
            results={itemList.results || []}
            onTeamAdded={onGroupAdded}
            onTeamUpdated={onGroupUpdated}
            updateIsSubmitting={updateIsSubmitting}
          />
        </>
      );
    case 'loading':
    default:
      return <Loader />;
  }
};

const GroupItems = ({
  parentId,
  domainTag,
  subGroup,
  results,
  onTeamAdded,
  onTeamUpdated,
  updateIsSubmitting
} : {
  parentId: string,
  domainTag: string,
  subGroup?: TGroup,
  results: TBrokerGroup[],
  onTeamAdded: (data: TBrokerGroup) => void,
  onTeamUpdated: (data: TBrokerGroup) => void,
  updateIsSubmitting: (state: boolean) => void,
}) => {
  const [isAddingNew, updateIsAddingNew] = useState(false);
  const [errors, updateErrors] = useState<TError[]>([]);

  const onAddTeam = (data: TBrokerGroup) => {
    updateIsSubmitting(true);
    handleAddGroup({
      name: data.name,
      title: data.title,
      parent_id: parentId
    }, domainTag, (error) => {
      updateErrors(() => processAPIError(error, true, 'masterService') as TError[]);
      updateIsSubmitting(false);
    }, (data) => {
      updateIsAddingNew(false);
      onTeamAdded(data);
    });
  };

  const onUpdateGroup = (data: TBrokerGroup, onSuccess?: () => void) => {
    updateIsSubmitting(true);
    handlePatchGroup({
      ...data
    }, data.id as string, (error) => {
      updateErrors(() => processAPIError(error, true) as TError[]);
      updateIsSubmitting(false);
    }, (data) => {
      updateIsAddingNew(false);
      onTeamUpdated(data);
      if (onSuccess) {
        onSuccess();
      }
    });
  };

  return (
    <TeamsListStyled>
      <GroupName>
        {translate({ key: `master.brokers.structure.${domainTag}.title` })}
      </GroupName>
      {(!results?.length && !isAddingNew) ? <GroupName>No results</GroupName> : (
        results.map(group => (
          <Group
            key={group.id}
            group={group}
            subGroup={subGroup}
            onSubmit={(data, onSuccess) => {
              onUpdateGroup(data, onSuccess);
            }}
            updateErrors={updateErrors}
          />))
      )}
      <div>
        {
          isAddingNew ? (
            <Group
              isEditing={true}
              onSubmit={(data) => {
                onAddTeam(data);
              }}
              onCancel={() => {
                updateIsAddingNew(false);
              }}
              updateErrors={updateErrors}
            />
          ) : (
            <Button onClick={() => updateIsAddingNew(true)}>
              {translate({ key: `master.brokers.structure.${domainTag}.add` })}
            </Button>
          )
        }
      </div>
      {errors?.length ? (
        renderErrors(errors)
      ) : null}
    </TeamsListStyled>

  );
};

const Group = ({
  group,
  subGroup,
  isEditing = false,
  onSubmit,
  onCancel,
  updateErrors
}: {
  group?: TBrokerGroup,
  subGroup?: TGroup,
  isEditing?: boolean,
  onSubmit: (data: TBrokerGroup, onSuccess?: () => void) => void,
  onCancel?: () => void,
  updateErrors: (errors: TError[]) => void,
}) => {
  const [form, updateForm] = useState(resetForm(group));

  const handleChange = (key?: string, value?: string) => {
    const touchedForm = { ...form };
    // @ts-ignore
    touchedForm[key] = value;
    updateForm(touchedForm);
    updateErrors([]);
  };

  return (
    <GroupStyled>
      <GroupStyledInner>
        <GroupStyledInnerMain>
          {isEditing ? (
            <Input
              label={translate({ key: 'master.brokers.structure.departments_tag' })}
              name="name"
              value={form.name}
              handleChange={handleChange}
              styles={{ width: '100%' }}
            />
          ) : (
            <div>
              <InputName>
                {translate({ key: `master.brokers.structure.${group?.domain_tag}_tag` })}
              </InputName>
              <GroupName>{group?.name}</GroupName>
            </div>
          )}
          {isEditing ? (
            <Input
              label={translate({ key: 'master.common.title' })}
              name="title"
              value={form.title}
              handleChange={handleChange}
              styles={{ width: '100%' }}
            />
          ) : (
            <div>
              <InputName>{translate({ key: 'master.common.title' })}</InputName>
              <GroupName>{group?.title}</GroupName>
            </div>
          )}
        </GroupStyledInnerMain>
      </GroupStyledInner>

      {isEditing ? (
        <GroupControls>
          <Button
            preset="secondary"
            onClick={() => {
              updateForm(resetForm(group));
              updateErrors([]);
              if (onCancel) {
                onCancel();
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onSubmit(form, () => {
                updateForm(resetForm(group));
              });
            }}
          >
            Save
          </Button>
        </GroupControls>
      ) : null}

      {(subGroup && group?.id) ? (
        <GroupStyled>
          <GroupList
            parentId={group.id as string}
            domainTag={subGroup.domainTag}
            subGroup={subGroup.subGroup}
          />
        </GroupStyled>
      ) : null}
    </GroupStyled>
  );
};

const resetForm = (group?: TBrokerGroup) => {
  let touchedForm: TBrokerGroup = {
    name: '',
    title: '',
    id: '',
    parent_id: ''
  };

  if (group) {
    touchedForm = {
      ...touchedForm,
      ...group
    };
  }

  return touchedForm;
};

export default DrawerProfile;
